import { useFetcher, Link } from "@remix-run/react";
import { Loader2, Send } from "lucide-react";
import * as React from "react";
import { Button, buttonVariants } from "#app/components/ui/button.tsx";
import { Textarea } from "#app/components/ui/textarea.tsx";
import { useToast } from "#app/hooks/use-toast.ts";

// Define the props type
interface ThreadMessageFormProps {
  reviewId: number;
  user: any; // Define a more specific user type if possible
}

// Define the response type for the fetch call
interface MessageResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

// Type guard to validate the response
function isMessageResponse(data: unknown): data is MessageResponse {
  return (
    data !== null &&
    typeof data === "object" &&
    "success" in data &&
    typeof (data as any).success === "boolean"
  );
}

export function ThreadMessageForm({ reviewId, user }: ThreadMessageFormProps) {
  // Note: We are not using Remix's fetcher here, but standard fetch
  // If you want to use useFetcher, the approach needs to change
  // as useFetcher expects actions/loaders, not arbitrary API endpoints.
  const formRef = React.useRef<HTMLFormElement>(null);
  const { toast } = useToast();
  const [messageText, setMessageText] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Handle form submission client-side using standard fetch
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Don't submit empty messages
    if (!messageText.trim() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Manually create form data
      const formData = new FormData();
      formData.append("reviewId", reviewId.toString());
      formData.append("userId", user.id);
      formData.append("message", messageText);

      // Submit via fetch to the API endpoint
      const response = await fetch("/api/thread-messages", {
        method: "POST",
        body: formData,
      });

      const rawData = await response.json();

      // Validate response data
      if (!isMessageResponse(rawData)) {
        throw new Error("Invalid response format");
      }

      const data = rawData;

      if (data.success) {
        // Clear the form
        setMessageText("");

        // Show success toast
        toast({
          title: "Mensagem enviada",
          description: "Sua mensagem foi enviada com sucesso",
        });

        // Manually trigger a refresh of the ThreadMessages component
        const event = new CustomEvent("refreshThreadMessages", {
          detail: { reviewId },
        });
        document.dispatchEvent(event);

        // Update the ThreadReplyCount display dynamically
        const countElement = document.querySelector(
          `[data-review-id="${reviewId}"] span:nth-child(2)`,
        );
        if (countElement) {
          const count = parseInt(countElement.textContent || "0", 10);
          const newCount = count + 1;
          countElement.textContent = newCount.toString();

          const textElement = document.querySelector(
            `[data-review-id="${reviewId}"] span:nth-child(3)`,
          );
          if (textElement) {
            textElement.textContent = newCount === 1 ? "resposta" : "respostas";
          }
        }
      } else {
        toast({
          title: "Erro",
          description: data.error || "Ocorreu um erro ao enviar a mensagem",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error submitting message:", error); // Log the actual error
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao enviar a mensagem",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validate reviewId
  if (!reviewId || isNaN(reviewId)) {
    return (
      <div className="text-center">
        <p className="text-sm text-red-500">ID da avaliação inválido</p>
      </div>
    );
  }

  if (!user) {
    // Get the current path to redirect back after login
    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : "";
    return (
      <div className="flex flex-col items-center justify-center space-y-3 py-2">
        <p className="text-sm text-muted-foreground">
          Faça login para participar da conversa
        </p>
        <Link
          to={`/login?redirectTo=${encodeURIComponent(currentPath)}`}
          className={buttonVariants({ variant: "default", size: "sm" })}
        >
          Entrar para responder
        </Link>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-3">
      <div className="space-y-1">
        <Textarea
          name="message"
          value={messageText}
          onChange={(e) => setMessageText(e.target.value)}
          placeholder="Escreva sua mensagem..."
          className="min-h-[100px] resize-none focus:border-blue-400 focus:ring-blue-400/20 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          required
        />
      </div>

      <div className="flex justify-end">
        <Button
          type="submit"
          size="sm"
          className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-500 dark:hover:bg-blue-600"
          disabled={isSubmitting || !messageText.trim()}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Enviar mensagem
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
